import React, { useState, useEffect, useRef } from 'react';

export default function SplashScreen({ onDone }) {
  const [phase, setPhase] = useState('playing'); // 'playing' | 'fading'
  const videoRef = useRef(null);
  const fadeTimer = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleEnd = () => startFade();
    video.addEventListener('ended', handleEnd);

    // Fallback: if video doesn't end within 12s, fade out anyway
    const fallback = setTimeout(startFade, 12000);

    return () => {
      clearTimeout(fallback);
      clearTimeout(fadeTimer.current);
      video.removeEventListener('ended', handleEnd);
    };
  }, []);

  const startFade = () => {
    if (phase === 'fading') return;
    setPhase('fading');
    fadeTimer.current = setTimeout(onDone, 900);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: '#060d1f', overflow: 'hidden',
      opacity: phase === 'fading' ? 0 : 1,
      transition: 'opacity 0.9s cubic-bezier(0.4, 0, 0.2, 1)',
      pointerEvents: phase === 'fading' ? 'none' : 'all',
    }}>

      {/* ── VIDEO ── */}
      <video
        ref={videoRef}
        src="/splash_screen.mp4"
        autoPlay
        muted
        playsInline
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />

      {/* ── VIGNETTE overlay (darkens edges, looks cinematic) ── */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: `
          radial-gradient(ellipse at center, transparent 40%, rgba(6,13,31,0.55) 100%),
          linear-gradient(to top, rgba(6,13,31,0.7) 0%, transparent 30%),
          linear-gradient(to bottom, rgba(6,13,31,0.5) 0%, transparent 20%)
        `,
      }} />

      {/* ── GEMINI LOGO COVER (bottom-right corner) ── */}
      <div style={{
        position: 'absolute', bottom: 0, right: 0,
        width: 220, height: 80,
        background: 'linear-gradient(135deg, transparent 0%, rgba(6,13,31,0.92) 55%, #060d1f 100%)',
        pointerEvents: 'none',
      }} />
      {/* Extra solid block for hard pixel coverage */}
      <div style={{
        position: 'absolute', bottom: 0, right: 0,
        width: 130, height: 40,
        background: '#060d1f',
        pointerEvents: 'none',
      }} />



      {/* ── SKIP BUTTON (top-right) ── */}
      <button
        onClick={startFade}
        style={{
          position: 'absolute', top: 24, right: 28,
          background: 'rgba(255,255,255,0.1)',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(255,255,255,0.18)',
          borderRadius: 20,
          color: 'rgba(255,255,255,0.8)',
          fontSize: 12, fontWeight: 600,
          padding: '6px 16px',
          cursor: 'pointer',
          letterSpacing: '0.05em',
          transition: 'all 0.2s',
          opacity: 1,
          fontFamily: "'Inter', system-ui, sans-serif",
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.18)'; e.currentTarget.style.color = '#fff'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = 'rgba(255,255,255,0.8)'; }}
      >
        Skip ›
      </button>

      {/* Keyframes injected inline */}
      <style>{`
        @keyframes splashPulse {
          0%, 100% { opacity: 0.6; }
          50%       { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
